/**
 * Where a visitor came from, remembered once and never overwritten.
 *
 * First touch wins: the Instagram post that brought someone in on Tuesday is the reason they bought on
 * Friday, not the Google search they used to find the site again. It is written the first time the page
 * loads and read again when a checkout is created, so every sale carries its origin into Stripe.
 *
 * First-party only: one string in this browser's own storage, no network, no third party, nothing that
 * follows anyone anywhere else. Referrers are unreliable by design (apps and privacy modes strip them),
 * which is why a `?ref=` or the usual `utm_*` on a link is worth more than any of this.
 */
const KEY = 'roardrive:source:v1';

interface Source { src: string; at: number; path: string }

const clean = (s: string) => s.replace(/[^\w .:/@+-]/g, '').slice(0, 120);

/** Called once on boot, before the router touches the URL. */
export function captureSource(): void {
  try {
    if (localStorage.getItem(KEY)) return;
    const q = new URLSearchParams(location.search);
    const tagged = q.get('ref') || [q.get('utm_source'), q.get('utm_medium'), q.get('utm_campaign')].filter(Boolean).join(' · ');
    let host = '';
    try { host = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, '') : ''; } catch { /* not a URL */ }
    // arriving from our own pages is not an origin, it is navigation: leave the slot open for a real one
    if (!tagged && host && host === location.hostname) return;
    const src = clean(tagged || host || 'direct');
    const rec: Source = { src: src || 'direct', at: Date.now(), path: location.pathname.slice(0, 80) };
    localStorage.setItem(KEY, JSON.stringify(rec));
  } catch { /* private mode, or storage full */ }
}

/** The remembered origin, or '' when this browser never got one. */
export function getSource(): string {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? String((JSON.parse(raw) as Source).src || '') : '';
  } catch {
    return '';
  }
}
